/**
 * ProviderCard — one provider tile in the Tier-1 hub grid.
 * Shows features count / enabled count / key configured indicator.
 */
import { motion } from 'framer-motion';
import { ChevronRight, CheckCircle2, XCircle } from 'lucide-react';
import { PROVIDER_META } from './constants';
import { ProviderLogo } from './ProviderLogo';

export function ProviderCard({ provider, language, onSelect }) {
  const meta = PROVIDER_META[provider.provider] || PROVIDER_META.Google;
  return (
    <motion.button
      type="button"
      whileHover={{ y: -4, scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={() => onSelect(provider)}
      className={`relative text-start bg-gradient-to-br ${meta.gradient} border rounded-2xl p-6 transition-all overflow-hidden group`}
      style={{ borderColor: `${meta.bg}50` }}
      data-testid={`ai-provider-card-${provider.id}`}
    >
      <div className="flex items-center gap-4 mb-4">
        <ProviderLogo provider={provider.provider} size={56} />
        <div className="flex-1">
          <h3 className="text-xl font-bold text-white">
            {language === 'ar' ? provider.label_ar : provider.label_en}
          </h3>
          <p className="text-xs text-white/60 mt-0.5">
            {language === 'ar' ? provider.tagline_ar : provider.tagline_en}
          </p>
        </div>
        <ChevronRight className={`h-6 w-6 text-white/40 group-hover:text-white/80 transition-colors ${language === 'ar' ? 'rotate-180' : ''}`} />
      </div>

      <div className="grid grid-cols-3 gap-3 mt-5">
        <StatBox
          label={language === 'ar' ? 'الميزات' : 'Features'}
          value={provider.feature_count}
        />
        <StatBox
          label={language === 'ar' ? 'مفعّلة' : 'Enabled'}
          value={provider.enabled_count}
          color={provider.enabled_count > 0 ? '#10b981' : '#71717A'}
        />
        <div className="bg-black/30 rounded-lg p-3 border border-white/5">
          <div className="text-[10px] text-white/50">
            {language === 'ar' ? 'المفتاح' : 'API Key'}
          </div>
          <div
            className="text-xs font-bold mt-1 flex items-center gap-1"
            style={{ color: provider.key_configured ? '#10b981' : '#ef4444' }}
            data-testid={`ai-key-status-${provider.id}`}
          >
            {provider.key_configured ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5" />
                {language === 'ar' ? 'متّصل' : 'Set'}
              </>
            ) : (
              <>
                <XCircle className="h-3.5 w-3.5" />
                {language === 'ar' ? 'مفقود' : 'Missing'}
              </>
            )}
          </div>
        </div>
      </div>
    </motion.button>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div className="bg-black/30 rounded-lg p-3 border border-white/5">
      <div className="text-[10px] text-white/50">{label}</div>
      <div
        className="text-lg font-bold text-white mt-0.5"
        style={color ? { color } : undefined}
      >
        {value}
      </div>
    </div>
  );
}
